import { AHIcon, AHText, Box, TouchableBox, useLayoutContext } from "arkh";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useDebounce } from "use-debounce";
import { Recents } from "./Recents";
import { SearchList } from "./SearchList";
import { SearchListInstructions } from "./SearchListInstructions";
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import {
  Keyboard,
  ViewStyle,
  StyleProp,
  useWindowDimensions,
  ScrollView,
} from "react-native";
import { graphql, useLazyLoadQuery, useSubscription } from "react-relay";
import { FilesScreenComponentQuery } from "./__generated__/FilesScreenComponentQuery.graphql";

import {
  FILE_SCREEN_SEARCH_BAR_HEIGHT,
  FileScreenSearchBar,
} from "./FilesScreenSearchBar";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { openFileType } from "./useOpenFile";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { useNavigationHeaderHeight } from "../../TopLevelNavigators/AHNavigationHeaderContainer";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  PendingFile,
  StoreFileUploadArea,
  getDocumentsFromPicker,
} from "./FileUploadArea";
import { FormattedMessage, useIntl } from "react-intl";
import { DropFileZone } from "./DropFileZone";

import cuid from "cuid";

import { HeaderButton } from "../../components";
import { useAlert } from "../../common/Alert";
import { useHasPermissions } from "../../OS/Login/usePermissions";
import { FilesEmptyComponent } from "./FilesEmptyComponent";
import { MAX_FILE_SIZE } from "./fileUtils/fileConstants";
import { NativeStackHeaderRightProps } from "@react-navigation/native-stack";

export type onPressIntegrationFileType = (_: {
  id: string;
  title: string;
}) => void;

type V = (props: NativeStackHeaderRightProps) => React.ReactNode;
type Props = {
  modal?: boolean;
  onPressIntegrationFile?: onPressIntegrationFileType;
  onPressFile: openFileType;
  includeIntegrationFiles: boolean;
  onShowAllFiles(): void;
  setHeaderRight?(fun: V): void;
  onUploadedFiles?(fileIds: string[]): void;
};

export function StoreFilesScreenComponent(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerFileScreenComponent {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function removeExtension(filename: string) {
  return filename.split(".").slice(0, -1).join(".");
}

function InnerFileScreenComponent(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<FilesScreenComponentQuery>(
    graphql`
      query FilesScreenComponentQuery {
        store {
          id
          ...usePermissions_store
          integrationFlyers {
            id
            eMaginId
            ...EmaginItem_IntegrationEmaginFlyer
          }
          suggestedFiles(first: 6) {
            ...SuggestionItem_File
            id
          }
          recentlyUploadedFiles {
            id
            ...SuggestionItem_File
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );
  const intl_tokens = useIntlTokens();
  const layout = useLayoutContext();
  const [query, setQuery] = React.useState("");
  const [debouncedQuery] = useDebounce(query, 800);
  const [mode, setMode] = useState<"RECENTS" | "SEARCH">("RECENTS");

  useSubscription({
    subscription: graphql`
      subscription FilesScreenComponentSubscription {
        filePendingStatusChange {
          filesQuota {
            quota
            used
          }
          recentlyUploadedFiles {
            id
            ...SuggestionItem_File
          }
        }
      }
    `,
    variables: {},
  });
  const searchAnimation = useSharedValue(0);
  const suggestedFiles = useMemo(
    () => data?.store.suggestedFiles ?? [],
    [data?.store.suggestedFiles]
  );

  useEffect(() => {
    if (mode === "SEARCH") {
      searchAnimation.value = withTiming(1, {
        duration: 200,
      });
    } else {
      searchAnimation.value = withTiming(0, {
        duration: 200,
      });
    }
  }, [mode, searchAnimation]);

  const searchBarContainerHeight =
    FILE_SCREEN_SEARCH_BAR_HEIGHT + layout.space.medium;

  const dimensions = useWindowDimensions();

  const searchBarContentHeight =
    dimensions.height -
    useNavigationHeaderHeight() -
    useSafeAreaInsets().top -
    layout.space.xsmall;

  const searchContainerAnimatedStyle = useAnimatedStyle(() => ({
    top: interpolate(
      searchAnimation.value,
      [0, 1],
      [layout.space.medium, layout.space.xsmall]
    ),
    right: interpolate(searchAnimation.value, [0, 1], [layout.space.medium, 0]),
    left: interpolate(searchAnimation.value, [0, 1], [layout.space.medium, 0]),
    height: interpolate(
      searchAnimation.value,
      [0, 1],
      [FILE_SCREEN_SEARCH_BAR_HEIGHT, searchBarContentHeight]
    ),
  }));

  const searchContainerStyle = useMemo<StyleProp<ViewStyle>>(
    () => ({
      position: "absolute",
      paddingTop: FILE_SCREEN_SEARCH_BAR_HEIGHT,
      borderRadius: layout.radius.medium,
      backgroundColor: "white",
      zIndex: 0,
      shadowColor: "#CCC",
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 1,
      shadowRadius: 3,
    }),
    [layout.radius.medium]
  );

  const flyers = useMemo(
    () =>
      props.includeIntegrationFiles
        ? (data.store.integrationFlyers?.filter((f) => f.eMaginId !== null) ??
          [])
        : [],
    [data.store.integrationFlyers, props.includeIntegrationFiles]
  );

  const insets = useSafeAreaInsets();

  const Alert = useAlert();

  const notifyErroringFiles = useCallback(() => {
    Alert.alert("Error", "Some of the selected files are too large.", [
      {
        style: "default",
        text: "OK",
      },
    ]);
  }, [Alert]);
  const [filesToBeUploaded, setFiles] = useState<PendingFile[]>([]);

  const onRequestUpload = useCallback(() => {
    getDocumentsFromPicker().then((res) => {
      if (res?.hasError) {
        notifyErroringFiles();
      }
      if (res) {
        setFiles(res.newFiles);
      }
    });
  }, [notifyErroringFiles]);

  const intl = useIntl();
  const uploadFileDescription = intl.formatMessage({
    description: "Upload File Description",
    defaultMessage: "Upload",
  });

  const canAdminFiles = useHasPermissions(data.store, "FILE_ADMIN");

  useLayoutEffect(() => {
    props.setHeaderRight?.(() =>
      canAdminFiles ? (
        <HeaderButton
          type="NEW"
          customLabel={uploadFileDescription}
          onPress={onRequestUpload}
        />
      ) : null
    );
  }, [
    canAdminFiles,
    notifyErroringFiles,
    onRequestUpload,
    props,
    uploadFileDescription,
  ]);

  const onDropFiles = (
    files: { file: File; type: string }[],
    nonApproved: {
      reason: "type" | "size";
      file: { file: File; type: string };
    }[]
  ): void => {
    if (nonApproved.length > 0) {
      notifyErroringFiles();
    }
    setFiles(
      files.map(({ file, type }) => ({
        file,
        type,
        name: removeExtension(file.name),
        id: cuid(),
        size: file.size,
      }))
    );
  };

  return (
    <DropFileZone
      deactivated={filesToBeUploaded.length > 0 || !canAdminFiles}
      type="FILE"
      maxSize={MAX_FILE_SIZE}
      onDropFiles={onDropFiles}
    >
      {data.store.recentlyUploadedFiles.length === 0 &&
      suggestedFiles.length === 0 &&
      flyers.length === 0 ? (
        <FilesEmptyComponent
          canAdminFiles={canAdminFiles}
          onRequestUpload={onRequestUpload}
        />
      ) : (
        <Box
          style={{
            flex: 1,
            paddingTop: searchBarContainerHeight,
          }}
        >
          <Box
            style={{
              position: "absolute",
              top: layout.space.medium,
              left: 0,
              right: 0,
              zIndex: 10,
              bottom: mode === "SEARCH" ? 0 : undefined,
              height: mode === "SEARCH" ? undefined : searchBarContainerHeight,
            }}
          >
            <FileScreenSearchBar
              value={query}
              onChangeText={setQuery}
              placeholder={intl_tokens.Search}
              multiline={false}
              returnKeyType="done"
              isSearching={mode === "SEARCH"}
              onFocus={() => {
                setMode("SEARCH");
              }}
              onCancel={() => {
                setQuery("");
                Keyboard.dismiss();
                setMode("RECENTS");
              }}
            />

            {mode === "SEARCH" &&
            query.length > 0 &&
            debouncedQuery.length > 0 ? (
              <SearchList query={debouncedQuery} openFile={props.onPressFile} />
            ) : null}
            {mode === "SEARCH" && query.length === 0 ? (
              <SearchListInstructions />
            ) : null}
          </Box>
          <ScrollView
            contentContainerStyle={{
              paddingHorizontal: layout.space.medium,
              paddingBottom: insets.bottom,
            }}
          >
            <Recents
              suggestedFiles={data.store.suggestedFiles}
              recentUploaded={data.store.recentlyUploadedFiles}
              integrationEmaginFlyers={flyers}
              modal={props.modal}
              onPressFile={props.onPressFile}
              onPressIntegrationFile={props.onPressIntegrationFile}
            />
            <TouchableBox
              onPress={props.onShowAllFiles}
              style={{ flexDirection: "row", alignItems: "center" }}
              spaceTop="medium"
              spaceBottom="xlarge"
            >
              <Box
                style={{
                  flex: 1,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                spaceVertical="medium"
                spaceHorizontal="medium"
                color="surface"
                cornerRadius="medium"
              >
                <AHText type="subtitle">
                  <FormattedMessage
                    defaultMessage="All Files"
                    description="Files Recents All Files button"
                  />
                </AHText>
                <AHIcon type="chevron-right" size="small" />
              </Box>
            </TouchableBox>
          </ScrollView>
          <Animated.View
            style={[searchContainerAnimatedStyle, searchContainerStyle]}
          />
        </Box>
      )}
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fk) => {
          return (
            <StoreFileUploadArea
              fetchKey={fk + filesToBeUploaded.map((f) => f.id).join("")}
              files={filesToBeUploaded}
              onDropedFiles={(dropedFiles) =>
                setFiles((prev) => [...prev, ...dropedFiles])
              }
              onRemoveFile={(id) => {
                setFiles(filesToBeUploaded.filter((f) => f.id !== id));
              }}
              onCancel={() => {
                setFiles([]);
              }}
              onUploadedFiles={props.onUploadedFiles}
            />
          );
        }}
      </LazyQueryNetworkBoundrary>
    </DropFileZone>
  );
}
